import { FC, HTMLAttributeAnchorTarget, ReactNode } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useAppSelector } from '@hooks/store';
import { CommonLoader, UnableToLoadData } from '@components/index';
import strings from '@localizedstrings/LocalisedStrings';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import './adviser.scss';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { ScreenState } from '@src/app/enums';
import { useTheme } from '@mui/material';
import { PhoneIphoneOutlined } from '@mui/icons-material';

interface StyledButtonProps {
  href: string;
  target: HTMLAttributeAnchorTarget;
  children: ReactNode;
}

export const StyledButton: FC<StyledButtonProps> = ({ href, children, target }): JSX.Element => {
  const theme = useTheme();
  return (
    <Button
      className="advisorButton"
      sx={{
        height: '2.625rem',
        textTransform: 'none',
        backgroundColor: theme.palette.primary.main,
      }}
      variant="contained"
      target={target}
      href={href}
    >
      {children}
    </Button>
  );
};

const AdviserPage: FC = (): JSX.Element => {
  const { adviser, portfolioInfoState } = useAppSelector(selectPortfolios);
  const theme = useTheme();

  if (portfolioInfoState === ScreenState.Loading) return <CommonLoader />;
  if (!adviser) return <UnableToLoadData />;

  return (
    <Container>
      <Box mt={5} mb={5} className="adviserContent">
        <Grid container spacing={2} justifyContent="center">
          <Grid item>
            <h5 className="heading">{strings.adviser}</h5>
            <p className="name" style={{ color: theme.palette.primary.main }}>
              {adviser?.name}
            </p>
            {adviser?.afsl && (
              <div className="infoRow">
                <HomeOutlinedIcon fontSize="large" sx={{ color: theme.palette.primary.main }}></HomeOutlinedIcon>
                <p style={{ paddingBottom: '3px' }} className="infoText">
                  {adviser?.afsl.name}
                </p>
              </div>
            )}
            {adviser?.emailAddress && (
              <div className="infoRow">
                <MailOutlineOutlinedIcon
                  fontSize="large"
                  sx={{
                    marginBottom: 0,
                    paddingBottom: 0,
                    color: theme.palette.primary.main,
                  }}
                />
                <p className="infoText" style={{ paddingBottom: '3px' }}>
                  {adviser?.emailAddress}
                </p>
              </div>
            )}
            {adviser?.mobilePhoneNumber && (
              <div className="infoRow">
                <PhoneIphoneOutlined
                  fontSize="large"
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                />
                <p className="infoText">{adviser?.mobilePhoneNumber}</p>
              </div>
            )}
            <Stack spacing={3} mt={6}>
              {adviser?.emailAddress && (
                <StyledButton href={`mailto:${adviser?.emailAddress}`} target="_self">
                  {strings.email}
                </StyledButton>
              )}
              {adviser?.calendarUrl && (
                <StyledButton target="_blank" href={adviser?.calendarUrl}>
                  {strings.scheduleAppointment}
                </StyledButton>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default AdviserPage;
